import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {LivenessQrApiService, LivenessQrStatus} from '@npck/auth-frontend-api';
import {OauthRedirectErrorCode} from '@npck/errors-api';
import {AnalyticsService} from '@npck/libs/analytics-sdk';
import {
    BusinessErrorCode,
    LOCALE_KEY,
    LocalizationService,
    REQUEST_ID_HEADER,
    rxSpinner,
    Spinner,
    toBusinessOrNull,
} from '@npck/libs/commons';
import {interval, mergeMap, Subscription} from 'rxjs';
import {AuthHolderTokenService} from '../../../domain/auth-holder-token.service';
import {OauthService} from '../../../domain/oauth.service';
import {AUTH_VIDEO_QR_FORMED} from '../../../infra/analytics-events';
import {Path} from '../../../path';

const LIVENESS_REQUEST_INTERVAL = 5_000;

@Component({
    selector: 'auth-liveness-create-qr',
    templateUrl: 'liveness-create-qr.component.html',
    standalone: false
})
export class LivenessCreateQrComponent implements OnInit, OnDestroy {
    @Input() qrAppearance: QrAppearance;
    QrAppearance = QrAppearance;
    Spinner = Spinner;

    qrId: string;
    qrCodeData: string;
    qrStatusSubscription: Subscription;

    constructor(
        private livenessQrApiService: LivenessQrApiService,
        private localizationService: LocalizationService,
        private oauthService: OauthService,
        private tokenService: AuthHolderTokenService,
        private analyticsService: AnalyticsService,
    ) {
    }

    ngOnInit(): void {
        this.livenessQrApiService.createLivenessQr()
            .pipe(rxSpinner('createQr'))
            .subscribe({
                next: response => {
                    this.qrId = response.id;
                    const baseUrl = `${window.location.protocol}//${window.location.host}`;
                    const url = new URL(`/${Path.QR_WARNING}/${this.qrId}`, baseUrl);
                    url.searchParams.append(LOCALE_KEY, this.localizationService.currentLanguage.locale);
                    this.qrCodeData = url.toString();
                    this.waitForLivenessStatus();
                    this.analyticsService.capture(AUTH_VIDEO_QR_FORMED, {
                        qr_id: this.qrId,
                        qr_bool: true
                    });
                },
                error: err => {
                    const businessError = toBusinessOrNull(err);
                    if (businessError?.error === BusinessErrorCode.LIVENESS_QR_EXPIRED) {
                        this.oauthService.handleOauthErrorAndRedirect(OauthRedirectErrorCode.liveness_qr_expired, businessError.requestId);
                    } else {
                        throw err;
                    }
                },
            });
    }

    ngOnDestroy(): void {
        this.qrStatusSubscription?.unsubscribe();
    }

    private waitForLivenessStatus(): void {
        this.qrStatusSubscription = interval(LIVENESS_REQUEST_INTERVAL)
            .pipe(mergeMap(() => this.livenessQrApiService.checkLivenessQrStatus(this.qrId, 'response')))
            .subscribe({
                next: response => {
                    const responseBody = response.body;
                    responseBody.status === LivenessQrStatus.SCANNED && Spinner.show('livenessQrScanned');
                    if (responseBody.status === LivenessQrStatus.FINISHED) {
                        this.tokenService.handleNextFactor();
                    } else if (responseBody.status === LivenessQrStatus.LIMIT_PER_SESSION_EXCEEDED) {
                        this.oauthService.handleOauthErrorAndRedirect(
                            OauthRedirectErrorCode.liveness_attempts_exceeded,
                            response.headers.get(REQUEST_ID_HEADER),
                        );
                    } else if (responseBody.status === LivenessQrStatus.FACE_NOT_MATCHED) {
                        this.oauthService.handleOauthErrorAndRedirect(
                            OauthRedirectErrorCode.gbdfl_face_not_matched,
                            response.headers.get(REQUEST_ID_HEADER),
                        );
                    } else if (responseBody.status === LivenessQrStatus.CANCELED) {
                        this.oauthService.handleOauthErrorAndRedirect(OauthRedirectErrorCode.auth_liveness_step_cancelled);
                    } else if (responseBody.status === LivenessQrStatus.CAMERA_FAILED) {
                        this.oauthService.handleOauthErrorAndRedirect(
                            OauthRedirectErrorCode.device_error,
                            response.headers.get(REQUEST_ID_HEADER),
                        );
                    } else if (responseBody.status === LivenessQrStatus.EXPIRED) {
                        this.oauthService.handleOauthErrorAndRedirect(
                            OauthRedirectErrorCode.liveness_qr_expired,
                            response.headers.get(REQUEST_ID_HEADER),
                        );
                    }
                },
            });
    }
}

export enum QrAppearance {
    CAMERA_FAILED = 'CAMERA_FAILED',
    MANUALLY = 'MANUALLY'
}
